<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { Button, Input, Modal, Select } from "@/components/ui";
import { Icon } from "@iconify/vue";
import { ErrorMessage, Field, Form, defineRule } from "vee-validate";
import { useEditProduct } from "@/composables/product/useEditProduct";
import { useProductNames } from "@/composables/product/useProductNames";
import { useToast } from "@/composables/ui/useToast";
import { ProductPriceTypes, ProductPurchaseTypes } from "@/utils/enums";
import type { ProductNameDto } from "@/dtos/ProductName.dto";

defineRule("required", (value: string, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

// Separate from "required" so a valid $0 price (allowed for fixed-price products) isn't
// rejected by the falsy check above.
defineRule("requiredNumber", (value: string | number, [fieldName = "Field"]) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return `${fieldName} is required`;
  }
  return true;
});

interface Props {
  selectedProduct: Record<string, unknown>;
  opportunityId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  closeEditProductModal: [];
  productUpdated: [data: unknown];
}>();

const $toast = useToast();
const store = useStore();
const { mutate: updateProduct, isPending } = useEditProduct();

const editProductObj = ref<{
  id: string | null;
  name: string | null;
  price: number | string | null;
  quantity: number;
  tax: number | string | null;
  purchaseType: string | null;
}>({
  id: null,
  name: null,
  price: null,
  quantity: 1,
  tax: null,
  purchaseType: null,
});

// Product name catalog (organization-scoped, for the "select existing product" dropdown)
const productNamesParams = computed(() => {
  const organizationId = store.getters.account?.data?.organizationId;
  if (!organizationId) return null;
  return { organizationId };
});
const { data: productNamesData, isLoading: productNamesLoading } =
  useProductNames(productNamesParams);
const productNameCatalog = computed<ProductNameDto[]>(() => productNamesData.value || []);

const productNameOptions = computed(() => {
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  // Always surface the product currently being edited, even if the catalog hasn't loaded it yet
  if (editProductObj.value.name) {
    seen.add(editProductObj.value.name);
    options.push({ value: editProductObj.value.name, label: editProductObj.value.name });
  }
  for (const product of productNameCatalog.value) {
    if (!product?.name || !product.isActive || seen.has(product.name)) continue;
    seen.add(product.name);
    options.push({ value: product.name, label: product.name });
  }
  return options;
});

const findCatalogProductByName = (name: string) => {
  return productNameCatalog.value.find((product) => product.name === name);
};

// A "fixed" priced product (set in Settings > Products) locks the Price field —
// reps can't override it on the deal. "Flexible" (or a brand-new name) stays editable.
const selectedCatalogProduct = computed(() =>
  findCatalogProductByName(editProductObj.value.name || "")
);
const isPriceLocked = computed(
  () => selectedCatalogProduct.value?.priceType === ProductPriceTypes.fixed
);

const isCreatingNewProductName = ref(false);
const newProductNameInput = ref<{ focus?: () => void } | null>(null);

const onProductNameSelected = (name: string, field: { onChange: (v: string) => void }) => {
  field.onChange(name);
  const match = findCatalogProductByName(name);
  if (match) {
    editProductObj.value.price = match.price ?? null;
  }
};

const startCreatingNewProduct = () => {
  isCreatingNewProductName.value = true;
  editProductObj.value.name = null;
  // Don't carry over a price (possibly locked) that belonged to the previously selected catalog product.
  editProductObj.value.price = null;
  nextTick(() => newProductNameInput.value?.focus?.());
};

const cancelCreatingNewProduct = () => {
  isCreatingNewProductName.value = false;
  editProductObj.value.name = null;
};

const showModal = computed(
  () =>
    props.selectedProduct &&
    typeof props.selectedProduct === "object" &&
    Object.keys(props.selectedProduct).length > 0
);

const purchaseTypeOptions = computed(() =>
  Object.values(ProductPurchaseTypes).map((type) => ({
    value: type,
    label: type,
  }))
);

function setValues(product: Record<string, unknown> | undefined) {
  if (!product || Object.keys(product).length === 0) return;
  editProductObj.value.id = (product.id as string) || null;
  editProductObj.value.name = (product.name as string) || null;
  editProductObj.value.price = (product.price as number) ?? null;
  editProductObj.value.quantity = (product.quantity as number) ?? 1;
  editProductObj.value.tax = (product.tax as number) ?? null;
  editProductObj.value.purchaseType = (product.purchaseType as string) || null;
}

function closeModal() {
  emit("closeEditProductModal");
}

function editProduct() {
  const id = editProductObj.value.id;
  const name = editProductObj.value.name;
  const purchaseType = editProductObj.value.purchaseType;
  if (!id || !name || !purchaseType) {
    $toast?.error("Product ID is required to edit. Please save the opportunity first.");
    return;
  }

  const payload = {
    id,
    name,
    price: parseFloat(String(editProductObj.value.price)) || 0,
    quantity: parseInt(String(editProductObj.value.quantity), 10) || 1,
    tax:
      editProductObj.value.tax != null ? parseFloat(String(editProductObj.value.tax)) : undefined,
    purchaseType,
    opportunityId: props.opportunityId,
  };

  updateProduct(payload, {
    onSuccess: (data) => {
      $toast?.success("Product updated successfully");
      emit("productUpdated", data);
      closeModal();
    },
    onError: (error: Error) => {
      $toast?.error(error.message || "Failed to update product");
    },
  });
}

watch(
  () => props.selectedProduct,
  (v) => setValues(v),
  { immediate: true, deep: true }
);
</script>

<template>
  <Modal
    v-if="showModal"
    :show="true"
    headerVariant="blue"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    size="md"
    @close="closeModal"
  >
    <template #header>
      <div class="product-header__text">
        <h2 class="product-header__title">Edit Product</h2>
        <p class="product-header__subtitle">Update product details</p>
      </div>
      <button type="button" class="product-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>

    <template #body>
      <Form id="edit-product-form" @submit="editProduct">
        <div class="product-form">
          <div class="product-form__row">
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="name"
                :rules="{ required: ['Name'] }"
                :model-value="editProductObj.name"
                @update:model-value="editProductObj.name = $event"
              >
                <Input
                  v-if="isCreatingNewProductName"
                  ref="newProductNameInput"
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Name"
                  type="text"
                  :required="true"
                  placeholder="Enter new product name"
                />
                <Select
                  v-else
                  :model-value="field.value"
                  @update:model-value="onProductNameSelected($event, field)"
                  @blur="field.onBlur"
                  :options="productNameOptions"
                  :loading="productNamesLoading"
                  label="Name"
                  :required="true"
                  placeholder="Select or search product"
                  searchable
                  search-placeholder="Search products..."
                  show-add-button
                  add-button-text="New product name"
                  @add-new="startCreatingNewProduct"
                />
              </Field>
              <button
                v-if="isCreatingNewProductName"
                type="button"
                class="product-form__back-link"
                @click="cancelCreatingNewProduct"
              >
                <Icon icon="tabler:corner-up-left" width="12" height="12" />
                Choose from existing products
              </button>
              <ErrorMessage name="name" class="error-message" />
            </div>
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="price"
                :rules="{ requiredNumber: ['Price'] }"
                :model-value="editProductObj.price"
                @update:model-value="editProductObj.price = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Price"
                  type="number"
                  :required="true"
                  :disabled="isPriceLocked"
                  placeholder="0.00"
                />
              </Field>
              <div v-if="isPriceLocked" class="product-form__lock-hint">
                <Icon icon="tabler:lock" width="11" height="11" />
                Fixed price — set in Settings
              </div>
              <ErrorMessage name="price" class="error-message" />
            </div>
          </div>
          <div class="product-form__row">
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="quantity"
                :model-value="editProductObj.quantity"
                @update:model-value="editProductObj.quantity = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Quantity"
                  type="number"
                  placeholder="1"
                />
              </Field>
            </div>
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="tax"
                :model-value="editProductObj.tax"
                @update:model-value="editProductObj.tax = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Tax %"
                  type="number"
                  placeholder="0.00"
                />
              </Field>
            </div>
          </div>
          <div>
            <Field
              name="purchaseType"
              :rules="{ required: ['Subscription Type'] }"
              :model-value="editProductObj.purchaseType"
              @update:model-value="editProductObj.purchaseType = $event"
              v-slot="{ field }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                :options="purchaseTypeOptions"
                label="Subscription Type"
                :required="true"
                placeholder="Select subscription type"
              />
            </Field>
            <ErrorMessage name="purchaseType" class="error-message" />
          </div>
        </div>
      </Form>
    </template>

    <template #footer>
      <Button variant="secondary" size="sm" @click="closeModal">Cancel</Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        form="edit-product-form"
        :loading="isPending"
      >
        Save Changes
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.product-form {
  padding: 4px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-form__row {
  display: flex;
  gap: 16px;
}

.product-form__field {
  flex: 1;
}

.error-message {
  margin-top: 4px;
  display: block;
  font-size: 11px;
  color: #dc3545;
}
.product-form__back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 0;
  background: none;
  border: none;
  color: #3e3efe;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
}
.product-form__back-link:hover {
  text-decoration: underline;
}
.product-form__lock-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: #9ca3af;
  font-size: 11px;
}
.product-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.product-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.product-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.product-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.product-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>
